import {
  faArrowUpRightFromSquare,
  faChevronDown,
  faChevronUp,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WarningSolid } from '@wfp/icons-react';
import { Button } from '@wfp/react';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { linkCitations } from 'src/redux/aiNarrative/markdown';
import { AiSourceReference, AiSourceUsage } from 'src/redux/aiNarrative/types';
import {
  getPlainText,
  sortSourceReferences,
  sourceNumber,
} from 'src/redux/aiNarrative/utils';
import { countWords } from 'src/utils';
import useVerticalResize from './useVerticalResize';

const HIGHLIGHT_MS = 2000;

const ResizeHandleIcon: FC = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <path
      d="M8.5 1.5 1.5 8.5M8.5 5.5 5.5 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const ResizableContent: FC<{
  html: string;
  label: string;
  className?: string;
  testId?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ html, label, className, testId, onClick }) => {
  const { ref, handleProps } = useVerticalResize();

  return (
    <>
      <div
        ref={ref}
        className={['ai-draft-preview-content', className]
          .filter(Boolean)
          .join(' ')}
        onClick={onClick}
        data-test-id={testId}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <button
        type="button"
        className="ai-draft-resize"
        aria-label={`Resize the ${label} box`}
        {...handleProps}
      >
        <ResizeHandleIcon />
      </button>
    </>
  );
};

const SOURCE_GROUPS: { usage: AiSourceUsage['usage']; label: string }[] = [
  { usage: 'content', label: 'Used for content' },
  { usage: 'style', label: 'Used for style and structure' },
  { usage: null, label: 'Other sources' },
];

const SourceItem: FC<{
  reference: AiSourceReference;
  expanded: boolean;
  highlighted: boolean;
  onToggle: () => void;
}> = ({ reference, expanded, highlighted, onToggle }) => {
  const text = reference.details || '';
  const detailsId = `ai-source-details-${reference.id}`;

  return (
    <li
      id={`ai-source-item-${sourceNumber(reference)}`}
      // Focusable only as a jump target, so a citation click lands somewhere a
      // screen reader can announce.
      tabIndex={-1}
      className={highlighted ? 'ai-draft-source-highlight' : undefined}
      data-test-id={`ai-source-${reference.id}`}
    >
      <div className="ai-draft-source-row">
        <span className="ai-draft-source-number">
          [{sourceNumber(reference)}]
        </span>
        {reference.url ? (
          <a
            href={reference.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${reference.filename} in a new tab`}
            className="ai-draft-source-link"
          >
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              aria-hidden="true"
            />
            <span className="ai-draft-source-title">{reference.filename}</span>
          </a>
        ) : (
          <span className="ai-draft-source-title">{reference.filename}</span>
        )}
        {reference.doc_type_label && (
          <span
            className="ai-draft-source-type"
            data-test-id={`ai-source-type-${reference.id}`}
          >
            {reference.doc_type_label}
          </span>
        )}
        {text && (
          <Button
            small
            kind="ghost"
            className="ai-draft-source-more"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={`How ${reference.filename} was used`}
            data-test-id={`ai-source-toggle-${reference.id}`}
          >
            How it was used
            <FontAwesomeIcon
              icon={expanded ? faChevronUp : faChevronDown}
              aria-hidden="true"
            />
          </Button>
        )}
      </div>
      {text && expanded && (
        <p className="ai-draft-source-details" id={detailsId}>
          {text}
        </p>
      )}
    </li>
  );
};

interface Props {
  draftHtml: string;
  currentContent?: string;
  references: AiSourceReference[];
  wordLimit: number;
  hasExistingContent: boolean;
  onAccept: () => void;
  onReject: () => void;
}

const DraftPreviewPanel: FC<Props> = ({
  draftHtml,
  currentContent,
  references,
  wordLimit,
  hasExistingContent,
  onAccept,
  onReject,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (id: string) =>
    setExpanded(current => ({ ...current, [id]: !current[id] }));

  const sortedReferences = useMemo(
    () => sortSourceReferences(references),
    [references],
  );

  const [highlighted, setHighlighted] = useState<number | null>(null);
  const [pendingJump, setPendingJump] = useState<number | null>(null);
  const highlightTimer = useRef<number | undefined>(undefined);

  const handleCitationClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const control = (event.target as HTMLElement).closest?.('.ai-citation');
    const source = Number(control?.getAttribute('data-source'));
    if (!control || !Number.isFinite(source)) {
      return;
    }

    const target = sortedReferences.find(
      reference => sourceNumber(reference) === source,
    );
    if (!target) {
      return;
    }

    setExpanded(current => ({ ...current, [target.id]: true }));
    setHighlighted(source);
    setPendingJump(source);
  };

  useEffect(() => {
    if (pendingJump === null) {
      return;
    }

    const item = document.getElementById(`ai-source-item-${pendingJump}`);
    item?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'nearest',
    });
    item?.focus({ preventScroll: true });
    setPendingJump(null);

    window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(
      () => setHighlighted(null),
      HIGHLIGHT_MS,
    );
  }, [pendingJump]);

  useEffect(() => () => window.clearTimeout(highlightTimer.current), []);

  const sanitizedDraft = useMemo(
    () => DOMPurify.sanitize(linkCitations(draftHtml)),
    [draftHtml],
  );

  const sanitizedCurrent = useMemo(
    () => (currentContent ? DOMPurify.sanitize(currentContent) : ''),
    [currentContent],
  );
  const showBefore = hasExistingContent && !!sanitizedCurrent;

  const draftWordCount = useMemo(
    () => countWords(getPlainText(sanitizedDraft)),
    [sanitizedDraft],
  );

  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    acceptButtonRef.current?.focus({ preventScroll: true });
  }, []);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') onReject();
  }

  return (
    <div
      className="ai-draft-preview-panel"
      data-test-id="ai-draft-preview"
      onKeyDown={handleKeyDown}
    >
      {showBefore && (
        <>
          <div className="ai-draft-preview-header ai-draft-before-header">
            Current narrative
          </div>
          <ResizableContent
            html={sanitizedCurrent}
            label="current narrative"
            className="ai-draft-before-content"
            testId="ai-draft-before"
          />
        </>
      )}
      <div className="ai-draft-preview-header">
        <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden="true" />
        AI-generated draft
      </div>
      <ResizableContent
        html={sanitizedDraft}
        label="AI-generated draft"
        onClick={handleCitationClick}
      />
      {hasExistingContent && (
        <div className="ai-draft-preview-note">
          Accepting will replace the current narrative content.
        </div>
      )}
      {draftWordCount > wordLimit && (
        <div
          className="ai-draft-preview-warning"
          data-test-id="ai-draft-word-warning"
        >
          <WarningSolid color="#E4002B" width="16" height="16" />
          The draft exceeds the word limit: {draftWordCount} / {wordLimit}
        </div>
      )}
      {sortedReferences.length > 0 && (
        <div className="ai-draft-sources">
          <div className="ai-draft-sources-title">Sources</div>
          {SOURCE_GROUPS.map(({ usage, label }) => {
            const group = sortedReferences.filter(
              reference => (reference.usage ?? null) === usage,
            );
            if (group.length === 0) {
              return null;
            }

            return (
              <div className="ai-draft-source-group" key={label}>
                <div className="ai-draft-source-group-title">{label}</div>
                <ul>
                  {group.map(reference => (
                    <SourceItem
                      key={reference.id}
                      reference={reference}
                      expanded={!!expanded[reference.id]}
                      highlighted={highlighted === sourceNumber(reference)}
                      onToggle={() => toggle(reference.id)}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      <div className="ai-draft-preview-actions">
        <Button
          kind="secondary"
          className="ai-draft-reject"
          onClick={onReject}
          data-test-id="ai-draft-reject"
        >
          Reject
        </Button>
        <Button
          ref={acceptButtonRef}
          className="ai-draft-accept"
          onClick={onAccept}
          data-test-id="ai-draft-accept"
        >
          Accept
        </Button>
      </div>
    </div>
  );
};

export default DraftPreviewPanel;
